import {
  FluentProvider as FluentUIProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { type ReactNode, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '@/stores/appStore';
import { ottoDarkTheme } from '@/theme/ottoDarkTheme';
import {
  ottoDarkTokenValues,
  ottoLightTokenValues,
} from '@/theme/ottoTokenValues';

type FluentProviderProps = {
  children: ReactNode;
};

export const FluentProvider = ({ children }: FluentProviderProps) => {
  // Subscribe only to the fields this root provider actually uses. Reading the
  // whole store here would re-render the entire app tree whenever unrelated
  // navigation state (selectedApp, selectedProject, tenant, location) changes.
  const { theme, reduceMotion } = useAppStore(
    useShallow((s) => ({ theme: s.theme, reduceMotion: s.reduceMotion })),
  );
  const fluentTheme = theme === 'dark' ? ottoDarkTheme : webLightTheme;

  // Inject Otto CSS custom properties on the document root per theme
  useEffect(() => {
    const root = document.documentElement;
    const vars = theme === 'dark' ? ottoDarkTokenValues : ottoLightTokenValues;
    const keys = Object.keys(vars).filter((k) => k.startsWith('--'));
    for (const key of keys) {
      root.style.setProperty(key, vars[key] ?? '');
    }
    return () => {
      for (const key of keys) {
        root.style.removeProperty(key);
      }
    };
  }, [theme]);

  // Mirror the user's reduce-motion preference to the document root so
  // global CSS rules and components can branch on `[data-reduce-motion]`
  // in addition to the OS-level `prefers-reduced-motion` media query.
  // 'always' force-enables reduced motion; 'system' defers to the media query.
  useEffect(() => {
    const root = document.documentElement;
    if (reduceMotion === 'always') {
      root.setAttribute('data-reduce-motion', 'true');
    } else {
      root.removeAttribute('data-reduce-motion');
    }
    return () => {
      root.removeAttribute('data-reduce-motion');
    };
  }, [reduceMotion]);

  return (
    <FluentUIProvider theme={fluentTheme} style={{ height: '100%' }}>
      {children}
    </FluentUIProvider>
  );
};
